import { useQuery } from '@tanstack/react-query';

import type {
  EscrowSendListReceivedOutput,
  EscrowSendListSentOutput,
  EscrowSendReceivedItem,
  EscrowSendSentItem,
} from '@infinex/main-service-contract/v2/client/escrowSend';

import type { QueryOptionsV2 } from '../config';
import { getTRPCClient } from '../config';

// The contract marks `escrowSend` optional (services/main-only surface), but
// the tRPC client is a Proxy, so the property always materialises at runtime —
// a missing route surfaces as a wire error, which consumers treat as an empty
// feed contribution. Hence the non-null assertions below.

export const escrowSentActivityQueryKey = () =>
  getTRPCClient().escrowSend!.listSent.queryKey();

export const escrowReceivedActivityQueryKey = () =>
  getTRPCClient().escrowSend!.listReceived.queryKey();

/**
 * First page (max 50) of the caller's private sends — the activity feed
 * source. The procedure no-ops to `disabled` while the flag is off, which —
 * like route-level errors (e.g. NOT_FOUND while the route is not deployed) —
 * reads as an empty feed contribution: `select` maps non-ready to `[]`, and
 * errors surface for the caller to settle as empty.
 */
export function useEscrowSentActivityQuery(
  options?: Omit<
    QueryOptionsV2<EscrowSendListSentOutput, EscrowSendSentItem[]>,
    'select' | 'retry'
  >
) {
  return useQuery(
    getTRPCClient().escrowSend!.listSent.queryOptions(
      { limit: 50 },
      {
        ...options,
        select: (output) => (output.status === 'ready' ? output.items : []),
        retry: false,
      }
    )
  );
}

/** First page (max 50) of the caller's claimed private receives. */
export function useEscrowReceivedActivityQuery(
  options?: Omit<
    QueryOptionsV2<EscrowSendListReceivedOutput, EscrowSendReceivedItem[]>,
    'select' | 'retry'
  >
) {
  return useQuery(
    getTRPCClient().escrowSend!.listReceived.queryOptions(
      { limit: 50 },
      {
        ...options,
        select: (output) => (output.status === 'ready' ? output.items : []),
        retry: false,
      }
    )
  );
}
